import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/solid';

interface GoogleDriveVideoEmbedProps {
  src: string;
  title?: string;
  className?: string;
  allowDownload?: boolean;
}

export const GoogleDriveVideoEmbed: React.FC<GoogleDriveVideoEmbedProps> = ({
  src,
  title = 'Video Preview',
  className = '',
  allowDownload = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [isFullBleed, setIsFullBleed] = useState(false);
  const [showFullscreenBtn, setShowFullscreenBtn] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetHideTimer = useCallback(() => {
    setShowFullscreenBtn(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setShowFullscreenBtn(false);
    }, 3500);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for window blur (which fires when user taps/clicks inside the iframe document)
  useEffect(() => {
    const handleWindowBlur = () => {
      resetHideTimer();
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [resetHideTimer]);

  useEffect(() => {
    if (isMobile) {
      resetHideTimer();
    }
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [isMobile, isFullBleed, resetHideTimer]);

  const formatEmbedUrl = (urlStr: string): string => {
    try {
      const urlObj = new URL(urlStr);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      for (let i = 0; i < pathParts.length; i++) {
        if (pathParts[i] === 'd' && i + 1 < pathParts.length) {
          return `https://drive.google.com/file/d/${pathParts[i + 1]}/preview`;
        }
      }
      const fileId = urlObj.searchParams.get('id');
      if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
    } catch (e) {}
    return urlStr.includes('/preview') ? urlStr : `${urlStr}/preview`;
  };

  const toggleFullscreen = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    resetHideTimer();
    if (!containerRef.current) return;

    if (isFullBleed || document.fullscreenElement) {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      setIsFullBleed(false);
    } else {
      setIsFullBleed(true);
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen().catch(() => {});
      }
    }
  };

  useEffect(() => {
    const handleFsChange = () => {
      if (!document.fullscreenElement) {
        setIsFullBleed(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const embedUrl = formatEmbedUrl(src);

  return (
    <div
      ref={containerRef}
      onClick={resetHideTimer}
      onTouchStart={resetHideTimer}
      className={`relative overflow-hidden bg-black ${
        isFullBleed
          ? 'fixed !inset-0 !z-[99999] !w-screen !h-screen !max-w-none !max-h-none !rounded-none'
          : `w-full h-full rounded-xl ${className}`
      }`}
    >
      <iframe
        src={embedUrl}
        title={title}
        frameBorder="0"
        scrolling="no"
        allow={
          isMobile
            ? 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            : 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'
        }
        allowFullScreen={!isMobile}
        className="absolute border-0 rounded-xl gdrive-iframe pointer-events-auto"
        style={
          isMobile
            ? {
                width: '300%',
                height: '300%',
                transform: 'scale(0.33333)',
                transformOrigin: 'top left',
                top: 0,
                left: 0,
              }
            : {
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
              }
        }
      ></iframe>

      {/* Block top-right pop-out icon from Google Drive unless download is explicitly allowed */}
      {!allowDownload && (
        <div
          className="absolute top-0 right-0 w-36 h-14 bg-transparent z-20 cursor-default"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Intercept taps on top area to show controls on mobile */}
      {isMobile && (
        <div
          onClick={resetHideTimer}
          onTouchStart={resetHideTimer}
          className="absolute top-0 left-0 right-16 h-16 z-30 cursor-pointer bg-transparent"
        />
      )}

      {/* Intercept taps on native bottom-right fullscreen button on mobile */}
      {isMobile && (
        <div
          onClick={toggleFullscreen}
          onTouchStart={resetHideTimer}
          className="absolute bottom-0 right-0 w-24 h-16 z-30 cursor-pointer bg-transparent"
          title="Toggle Fullscreen"
        />
      )}

      {/* Floating Fullscreen Toggle Button on Top Right (Mobile Only, Auto-Hides after 3.5s, Re-appears on tap/click inside video iframe) */}
      {isMobile && (
        <button
          type="button"
          onClick={toggleFullscreen}
          onTouchStart={resetHideTimer}
          className={`absolute top-3 right-3 p-2.5 rounded-full bg-black/70 text-white backdrop-blur-md z-40 shadow-xl border border-white/20 hover:bg-black/90 transition-opacity duration-300 ${
            showFullscreenBtn ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          title={isFullBleed ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          {isFullBleed ? (
            <ArrowsPointingInIcon className="w-5 h-5 text-white" />
          ) : (
            <ArrowsPointingOutIcon className="w-5 h-5 text-white" />
          )}
        </button>
      )}
    </div>
  );
};

export default GoogleDriveVideoEmbed;
